import React from "react"
import {compose} from "redux"
import {useHistory} from "react-router-dom"
import {useTranslation} from "react-i18next"

// HOCs
import withUser, {WithUserProps} from "../../../modules/auth/hoc/withUser"
import withSignOut, {WithSignOutProps} from "../../../modules/auth/hoc/withSignOut"

// Context
import {useUiSkin} from "../../../modules/ui-skin/context/UiSkinContext"

// Constants
import {UiSkin} from "../../../modules/ui-skin/constants"
import {AppRoute} from "../../../modules/routing/constants"
import {isActiveLanguage, languageLabel, supportedLanguages} from "../../../translations/languages"

// Components
import BrandMark from "../BrandMark"
import Icon from "../Icon"

// Utils
import {displayName, initials} from "../../../utils/ModernFormat"

// Styles
import useStyles from "./styles"

const SKINS: Array<{value: UiSkin, labelKey: string}> = [
    {value: "classic", labelKey: "modern.classic"},
    {value: "modern", labelKey: "modern.modern"},
];

type ModernAppBarProps = {
    /** Rendered immediately after the brand — the record page passes its meta block here. */
    children?: React.ReactNode
    showSignOut?: boolean
} & WithUserProps & WithSignOutProps

/**
 * @param props
 */
function ModernAppBar(props: ModernAppBarProps) {
    const styles = useStyles();
    const history = useHistory();
    const [t, i18n] = useTranslation("common");
    const {skin, setSkin, isDark, toggleTheme} = useUiSkin();
    const {user, signOut, children, showSignOut = true} = props;

    return (
      <header className={styles.appbar}>
          <button
            type="button"
            className={styles.brand}
            onClick={() => history.push(AppRoute.Home)}
            aria-label={t("modern.dashboard")}
            title={t("modern.dashboard")}
          >
              <BrandMark height={20} className={styles.brandmark}/>
          </button>

          {children}
          {!children && <span className={styles.rowbreak} aria-hidden="true"/>}

          <div className={styles.spacer}/>

          <div className={styles.uimode} role="group" aria-label={t("modern.view_mode")}>
              {SKINS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.uimode_btn} ${skin === option.value ? styles.uimode_btn__active : ""}`}
                  aria-pressed={skin === option.value}
                  onClick={() => setSkin(option.value)}
                >
                    {t(option.labelKey)}
                </button>
              ))}
          </div>

          <button
            type="button"
            className={`${styles.iconbtn} ${styles.iconbtn__icononly} ${isDark ? styles.iconbtn__active : ""}`}
            onClick={toggleTheme}
            aria-pressed={isDark}
            title={t("modern.reading_room")}
            aria-label={t("modern.reading_room")}
          >
              <Icon name="moon"/>
          </button>

          <div className={styles.lang} role="group" aria-label={t("modern.language")}>
              {supportedLanguages.map(language => (
                <button
                  key={language}
                  type="button"
                  className={`${styles.langbtn} ${isActiveLanguage(i18n.language, language) ? styles.langbtn__active : ""}`}
                  aria-pressed={isActiveLanguage(i18n.language, language)}
                  onClick={() => i18n.changeLanguage(language)}
                >
                    {languageLabel(language)}
                </button>
              ))}
          </div>

          {user !== null && (
            <div className={styles.user}>
                <span className={styles.avatar}>{initials(user)}</span>
                <span className={styles.who}>
                    <b>{displayName(user, t("modern.doctor"), t("modern.doctor_title"))}</b>
                    <span>{user.email || user.username || ""}</span>
                </span>
            </div>
          )}

          {showSignOut && user !== null && (
            <button type="button" className={styles.iconbtn} onClick={signOut} title={t("sign_out")}>
                <Icon name="signout"/>
                {t("sign_out")}
            </button>
          )}
      </header>
    )
}

export {ModernAppBar}

export default compose(
    withUser,
    withSignOut,
)(ModernAppBar) as React.ComponentType<{children?: React.ReactNode, showSignOut?: boolean}>
